import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Bed, IndianRupee, ChevronRight } from 'lucide-react';

const BADGE = {
  featured: 'bg-amber-500 text-white',
  premium:  'bg-purple-600 text-white',
  basic:    'bg-blue-600 text-white',
};

function SimilarCard({ property }) {
  const { property_id, title, city, area, bhk_type, property_type, rent, images, listing_type, furnishing } = property;
  const imgSrc = images?.[0];
  const displayType = bhk_type ? `${bhk_type} ${property_type}` : property_type;

  return (
    <Link
      to={`/property/${property_id.replace(/_/g, '-')}`}
      className="group flex-shrink-0 w-64 bg-white rounded-2xl border border-slate-100 shadow-[0_2px_12px_rgb(0,0,0,0.05)] overflow-hidden hover:shadow-[0_6px_24px_rgb(0,0,0,0.10)] hover:-translate-y-1 transition-all duration-300"
      data-testid={`similar-card-${property_id}`}
    >
      <div className="relative h-40 w-full overflow-hidden bg-slate-100">
        {imgSrc ? (
          <img src={imgSrc} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-slate-50">
            <span className="text-slate-300 text-4xl">🏠</span>
          </div>
        )}
        {BADGE[listing_type] && (
          <span className={`absolute top-2.5 left-2.5 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${BADGE[listing_type]}`}>
            {listing_type}
          </span>
        )}
      </div>
      <div className="p-3.5">
        <h4 className="font-semibold text-slate-900 text-sm line-clamp-1 group-hover:text-blue-700 transition-colors mb-1">
          {title || displayType}
        </h4>
        <div className="flex items-center gap-1 text-slate-500 text-xs mb-2">
          <MapPin className="w-3 h-3 text-blue-500 flex-shrink-0" />
          <span className="truncate">{area}{area && city ? ', ' : ''}{city}</span>
        </div>
        <div className="flex items-center gap-1.5 mb-2">
          {bhk_type && (
            <span className="flex items-center gap-1 text-xs text-slate-600 bg-slate-50 px-2 py-0.5 rounded-md">
              <Bed className="w-3 h-3" />{bhk_type}
            </span>
          )}
          {furnishing && furnishing !== 'unfurnished' && (
            <span className="text-xs text-slate-600 bg-slate-50 px-2 py-0.5 rounded-md capitalize">{furnishing}</span>
          )}
        </div>
        <div className="flex items-center gap-0.5 pt-2 border-t border-slate-100">
          <IndianRupee className="w-3.5 h-3.5 text-blue-700" />
          <span className="text-base font-bold text-blue-700">{Number(rent).toLocaleString('en-IN')}</span>
          <span className="text-slate-400 text-xs ml-1">/mo</span>
        </div>
      </div>
    </Link>
  );
}

export default function SimilarProperties({ propertyId }) {
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!propertyId) return;
    setLoading(true);
    fetch(`/api/properties/${propertyId}/similar`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setSimilar(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [propertyId]);

  if (loading || similar.length === 0) return null;

  return (
    <section className="mt-10 mb-4" data-testid="similar-properties-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Similar Properties
        </h2>
        <Link
          to="/search"
          className="flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800 transition-colors"
          data-testid="similar-view-all"
        >
          View All <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3 -mx-1 px-1 scrollbar-thin scrollbar-thumb-slate-200">
        {similar.map(p => (
          <SimilarCard key={p.property_id} property={p} />
        ))}
      </div>
    </section>
  );
}
